/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useSuspenseQuery } from '@apollo/client';
import { Menu, rem, useMantineTheme, Text, Group, Stack, Avatar } from '@mantine/core';
import { RiSettings3Line, RiLogoutBoxRLine, RiFeedbackLine } from '@remixicon/react';
import { useTranslation } from 'react-i18next';

import { UserProfileForSettingDocument } from '@fai/friendli-suite/generated/client/graphql';
import { UserAvatarIcon } from '@fai/svgr';
import { TextVariants } from '@fai/ui/components/typography/variants';
import type { PropsWithChildren } from '@fai/util/types';

export function UserEmail() {
  const theme = useMantineTheme();
  const {
    data: { clientSession },
  } = useSuspenseQuery(UserProfileForSettingDocument);

  return (
    <Group gap={theme.pf.layout['3xs']} px={theme.pf.layout.xs} py={rem(9.5)} wrap="nowrap">
      <Avatar
        size={rem(20)}
        src={clientSession?.user.avatarUrl}
        styles={{ placeholder: { border: 'none' } }}
      >
        <UserAvatarIcon size={20} />
      </Avatar>
      <Text c={theme.pf.color.fg.neutral.strong} truncate={true} variant={TextVariants.BodyMd}>
        {clientSession?.user.email ?? '-'}
      </Text>
    </Group>
  );
}

export function UserSettings({ onClick }: { onClick: VoidFunction }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Menu.Item
      leftSection={<RiSettings3Line color={theme.pf.color.fg.neutral.subtle} size={16} />}
      px={theme.pf.layout['2xs']}
      py={rem(9.5)}
      onClick={onClick}
    >
      <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.BodyMd}>
        {t('user_settings.user_settings')}
      </Text>
    </Menu.Item>
  );
}

export function Feedback({ onClick }: { onClick: VoidFunction }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Menu.Item
      leftSection={<RiFeedbackLine color={theme.pf.color.fg.neutral.subtle} size={16} />}
      px={theme.pf.layout['2xs']}
      py={rem(9.5)}
      onClick={onClick}
    >
      <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.BodyMd}>
        {t('user_settings.feature_request_and_feedback')}
      </Text>
    </Menu.Item>
  );
}

export function SignOut({ onClick }: { onClick: () => Promise<void> }) {
  const theme = useMantineTheme();
  const { t } = useTranslation();

  return (
    <Menu.Item
      leftSection={<RiLogoutBoxRLine color={theme.pf.color.fg.neutral.subtle} size={16} />}
      px={theme.pf.layout['2xs']}
      py={rem(9.5)}
      onClick={onClick}
    >
      <Text c={theme.pf.color.fg.neutral.strong} variant={TextVariants.BodyMd}>
        {t('auth.sign_out')}
      </Text>
    </Menu.Item>
  );
}

export function DropDown({ children }: PropsWithChildren) {
  return (
    <Menu.Dropdown miw={rem(270)} p={0}>
      <Stack gap={0}>{children}</Stack>
    </Menu.Dropdown>
  );
}

DropDown.displayName = 'ProfileDropDown';

DropDown.UserEmail = UserEmail;
DropDown.UserSettings = UserSettings;
DropDown.Feedback = Feedback;
DropDown.SignOut = SignOut;
